import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { get, kebabCase } from 'lodash';
import moment from 'moment';
import {
  Text, ArticleTeaser
} from '@comicrelief/component-library';
import { getImage } from 'gatsby-plugin-image';

import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import HeroBanner from '../../components/HeroBanner/HeroBanner';
import prizeDefaultImage from '../../../static/assets/news-default.jpg';

import {
  Wrapper,
  Container,
  Grid,
  Items,
  Section,
  Title,
  Closed,
  Live
} from '../../components/Prize/Prizes.styles';

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';

const Prizes = (props) => {
  const prizes = get(props, 'data.allContentfulPrizes.edges');
  const prizesHeader = get(props, 'data.allContentfulHeroBanner.edges');
  const [todaysDate, setTodaysDate] = useState(null);

  const today = moment();

  const handleGtm = (title, id, index) => {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      ecommerce: {
        click: {
          actionField: { list: 'prizes homepage listing' },
          products: [{
            id,
            name: title,
            price: 0.00,
            brand: 'cr-prizes',
            category: 'rednoseday',
            variant: 'none',
            position: index
          }]
        }
      },
      event: 'productClick'
    });
  };

  useEffect(() => {
    setTodaysDate(today);
  }, [prizes]);

  const currentPrizes = prizes.filter((prize) => {
    const prizeEndDate = moment(prize.node.endDate);
    return prizeEndDate.diff(todaysDate) > 0;
  });

  const closedPrizes = prizes.filter((prize) => {
    const prizeEndDate = moment(prize.node.endDate);
    return prizeEndDate.diff(todaysDate) < 0 && prize.node.winner !== true;
  });

  const winnerPrizes = prizes.filter(prize => prize.node.winner);

  // To match the new IA (Information Architecture) from Numiko:
  const parentPages = [
    { title: 'Get Involved', path: 'getinvolved' }];

  const thisPageTitle = 'Prizes';

  return (
    <Layout>
      <Wrapper>

        <Breadcrumb
          thisPageTitle={thisPageTitle}
          parentPages={parentPages}
        />

        {prizesHeader && prizesHeader.length > 0 ? (
          <HeroBanner data={prizesHeader[0].node} />
        ) : null}
        <Container>
          <Section>
            {currentPrizes.length > 0 ? (
              <>
                <Title>
                  <Text tag="h2">Open Prize Draws</Text>
                </Title>
                <Grid>
                  {currentPrizes.map(({ node }, index) => {
                    const {
                      featuredImage, path, title, id, category
                    } = node;

                    // Extract old format data for ArticleTeaser component compatibility
                    const thisImageData = getImage(featuredImage);
                    const thisImageFallback = thisImageData?.images?.fallback?.src;
                    const thisImageSrcSet = thisImageData?.images?.fallback?.srcSet
                                  || thisImageData?.images?.sources?.[0]?.srcSet;
                    const thisImageLow = thisImageData?.placeholder?.fallback || prizeDefaultImage;
                    const thisImageDesc = featuredImage?.description ?? '';
                    const URL = `/prizes/${kebabCase(category.name)}/${path}`;

                    return (
                      <Items key={id}>
                        <Live
                          onClick={() => handleGtm(title, id, index)}
                        >
                          <ArticleTeaser
                            href={URL}
                            image={thisImageFallback}
                            images={thisImageSrcSet}
                            imageLow={thisImageLow}
                            title={title}
                            alt={thisImageDesc}
                            time=""
                          />
                        </Live>
                      </Items>
                    );
                  })}
                </Grid>
              </>
            ) : null}
            {winnerPrizes.length > 0 ? (
              <>
                <Title>
                  <Text tag="h2">See the winners</Text>
                </Title>
                <Grid>
                  {winnerPrizes.map(({ node }) => {
                    const {
                      featuredImage, path, title, id, category
                    } = node;

                    // Extract old format data for ArticleTeaser component compatibility
                    const imageData = getImage(featuredImage);
                    const fallback = imageData?.images?.fallback?.src || prizeDefaultImage;
                    const images = imageData?.images?.fallback?.srcSet
                    || imageData?.images?.sources?.[0]?.srcSet || prizeDefaultImage;
                    const imageLow = imageData?.placeholder?.fallback || prizeDefaultImage;
                    const description = featuredImage ? featuredImage.description : '';
                    const URL = `/prizes/${kebabCase(category.name)}/${path}`;

                    return (
                      <Items key={id}>
                        <ArticleTeaser
                          href={URL}
                          image={fallback}
                          images={images}
                          imageLow={imageLow}
                          title={title}
                          alt={description}
                          time=""
                        />
                      </Items>
                    );
                  })}
                </Grid>
              </>
            ) : null}
            {closedPrizes.length > 0 ? (
              <>
                <Title>
                  <Text tag="h2">Closed Prize draws</Text>
                </Title>
                <Grid>
                  {closedPrizes.map(({ node }) => {
                    const {
                      featuredImage, path, title, id, category
                    } = node;

                    // Extract old format data for ArticleTeaser component compatibility
                    const imageData = getImage(featuredImage);
                    const fallback = imageData?.images?.fallback?.src || prizeDefaultImage;
                    const images = imageData?.images?.fallback?.srcSet
                      || imageData?.images?.sources?.[0]?.srcSet || prizeDefaultImage;
                    const imageLow = imageData?.placeholder?.fallback || prizeDefaultImage;
                    const description = featuredImage ? featuredImage.description : '';
                    const URL = `/prizes/${kebabCase(category.name)}/${path}`;

                    return (
                      <Items key={id}>
                        <Closed>
                          <ArticleTeaser
                            href={URL}
                            image={fallback}
                            images={images}
                            imageLow={imageLow}
                            title={title}
                            alt={description}
                            time=""
                          />
                        </Closed>
                      </Items>
                    );
                  })}
                </Grid>
              </>
            ) : null}
          </Section>
        </Container>
      </Wrapper>
    </Layout>
  );
};

export default Prizes;

export const Head = props => (
  <SEO
    allData={props}
    pageType={SEO_PAGE_TYPES.PRIZES}
    description="Win incredible, money-can't-buy prizes and experiences whilst taking on poverty and injustice at the same time. Enter now. Good luck!"
    title="Prizes"
  >
    {(siteUrl, linkedDataObj) => (
      <script type="application/ld+json" key="carousel">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: linkedDataObj?.thesePrizeEdges.map((prize, position) => ({
            '@type': 'ListItem',
            position,
            url: `${siteUrl}/prizes/${prize.node.path}`
          }))
        })}
      </script>
    )}
  </SEO>
);

export const pageQuery = graphql`
query getPrizes($skip: Int, $limit: Int) {
  allContentfulPrizes(skip: $skip, limit: $limit, sort: {endDate: DESC}) {
    totalCount
    pageInfo {
      perPage
      currentPage
      hasPreviousPage
      hasNextPage
      itemCount
      pageCount
    }
    edges {
      node {
        title
        path
        id
        category {
          name
        }
        startDate(formatString: "Do MMMM YYYY")
        endDate
        winner
        featuredImage {
          description
          ...Picture
        }
      }
    }
  }
  allContentfulHeroBanner(filter: {title: {eq: "Prizes Header - DO NOT REMOVE"}}) {
    edges {
      node {
        ...HeroBanner
      }
    }
  }
}
`;
